import ImageModalDsk from 'components/common/ImageModalDsk'
import ImageModalMbl from 'components/common/ImageModalMbl'
import { BLUR_DATA_URL, MEDIA_URL } from 'config/constants'
import useSlug from 'hooks/useSlug'
import {
  isAbsent,
  isEmptyObj,
  isNonEmptyArray,
  isNonEmptyObj,
  isSame,
  slashSiteLinks,
} from 'lib/utils'
import Head from 'next/head'
import Image from 'next/image'
import { PartnerPageProps } from 'pages/partners/[slug]'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAbout } from 'store/api'
// import PartnerForm from './PartnerForm'

// SEO: мета-описания партнёрских страниц (рекомендация Вебмастера «Нет мета-описаний»).
// Кураторские тексты по slug (согласованы с PO 28.08); для остальных партнёров —
// первый абзац текста страницы без разметки, до 160 символов.
const PARTNER_DESCRIPTIONS: Record<string, string> = {
  confael:
    'MOSCOW FOOD — партнёр шоколадной фабрики «Конфаэль»: премиальные шоколадные подарки для корпоративных поводов и событий.',
  'dostavka-zhivyih-tsvetov':
    'Доставка живых цветов в Москве: букеты, собранные вручную опытными флористами, — элегантное дополнение к вашему кейтерингу и подаркам.',
  parnters:
    'MOSCOW FOOD совместно с благотворительным фондом «Гольфстрим»: помощь людям с тяжёлыми диагнозами с 2011 года.',
}

let partnerDescription = (partner: PartnerPageProps['partner']) => {
  const curated = PARTNER_DESCRIPTIONS[partner?.slug ?? '']
  if (curated) return curated
  const plain = String(partner?.text ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return plain ? plain.slice(0, 157).replace(/\s+\S*$/, '') + '…' : undefined
}

let PartnerView = ({ partner: partnerSSR }: PartnerPageProps) => {
  let { slug } = useSlug()
  let [imgIdx, setImgIdx] = useState<number | null>(null)
  let [isMblModalOpen, setIsMblModalOpen] = useState(false)
  let [isDskModalOpen, setIsDskModalOpen] = useState(false)

  const about = useSelector(selectAbout('ru'))

  let handleModalOpen = useCallback(
    (idx: number, type: 'dsk' | 'mbl') => {
      setImgIdx(idx)
      isSame(type, 'mbl')
        ? setIsMblModalOpen(true)
        : setIsDskModalOpen(true)
    },
    []
  )

  let partner = useMemo(
    () =>
      isNonEmptyObj(partnerSSR) && isSame(partnerSSR.slug, slug)
        ? partnerSSR
        : about.find((i) => i.slug === slug),
    [partnerSSR, slug, about]
  )

  let images = partner?.images.map((img) => ({
    src: `${MEDIA_URL}${img.src}`,
    height: 1,
    width: 1,
    blurDataURL: '',
  }))

  if (isAbsent(partner) || isEmptyObj(partner))
    return (
      <div className='flex flex-col px-4'>
        <h1 className='mt-20 text-2xl xl:text-4xl text-center leading-snug'>
          Ошибка 404 <br /> Нет такой страницы
        </h1>
      </div>
    )

  return (
    <>
      <Head>
        <title>{partner.name}</title>
        <meta name='description' content={partnerDescription(partner) ?? partner.name} />
        <meta name='apple-mobile-web-app-title' content={partner.name} />
        <meta property='og:site_name' content={partner.name} />
      </Head>

      <div className='mb-12 pt-4 lg:pt-0 custom-px'>
        <h1 className='mb-4 pt-4 lg:pt-0 font-unbounded text-2xl lg:text-5xl tracking-tight md:tracking-tight leading-snug md:leading-snug'>
          {partner.name}
        </h1>

        <div
          className='mb-8 flex flex-col gap-2 text-base2 leading-snug text-bodyGrey'
          dangerouslySetInnerHTML={{ __html: slashSiteLinks(partner.text ?? '') }}
        ></div>

        {/* <PartnerForm /> */}

        {isNonEmptyArray(images) && (
          <div className='mt-6 py-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 border-t border-lightGrey'>
            {partner &&
              partner.images.map((pic, idx) => (
                <div key={pic.src}>
                  <div className='hidden md:block'>
                    <div className='relative aspect-w-6 aspect-h-5 cursor-zoom-in'>
                      <Image
                        onClick={() => handleModalOpen(idx, 'dsk')}
                        placeholder='blur'
                        blurDataURL={BLUR_DATA_URL}
                        src={`${MEDIA_URL}${pic.src}`}
                        alt=''
                        layout='fill'
                        objectFit='cover'
                      />
                    </div>
                  </div>

                  <div className='md:hidden'>
                    <div className='aspect-w-6 aspect-h-5 relative'>
                      <Image
                        onClick={() => handleModalOpen(idx, 'mbl')}
                        placeholder='blur'
                        blurDataURL={BLUR_DATA_URL}
                        src={`${MEDIA_URL}${pic.src}`}
                        alt=''
                        layout='fill'
                        objectFit='cover'
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {isNonEmptyArray(images) && (
        <>
          <ImageModalDsk
            images={images}
            idx={imgIdx ?? 0}
            src={partner.images[imgIdx ?? 0]!.src}
            isOpen={isDskModalOpen}
            setIsOpen={setIsDskModalOpen}
          />

          <ImageModalMbl
            src={partner.images[imgIdx ?? 0]!.src}
            idx={imgIdx ?? 0}
            images={images}
            isOpen={isMblModalOpen}
            setIsOpen={setIsMblModalOpen}
          />
        </>
      )}
    </>
  )
}

export default PartnerView
